import { z } from 'zod';
import { broadcasterModuleSchema, broadcasterThemeSchema } from './broadcaster';
import {
  ageSchema,
  countryCodeSchema,
  entityIdSchema,
  isoDateTimeSchema,
  localeSchema,
} from './common';
import { MEDIA_MIME_TYPES, hostedPathSchema } from './media';
import { objectiveIdSchema } from './mission';
import {
  contentFormatSchema,
  contentWarningSchema,
  creditSchema,
  interestTopicSchema,
  seriesRefSchema,
} from './content';

/**
 * Public, guest-facing catalogue shape (produced by `buildPublicCatalogue` in @ebu/domain and
 * served read-only by the API). It never contains drafts, rights evidence, staff data or media
 * locations for items that are not playable in the selected country.
 */

export const publicCatalogueQuerySchema = z.strictObject({
  broadcaster: entityIdSchema,
  locale: localeSchema,
});
export type PublicCatalogueQuery = z.infer<typeof publicCatalogueQuerySchema>;

/** Why listed metadata cannot be played (content referenced but unavailable for play). */
export const UNAVAILABLE_REASONS = [
  'rights-not-started',
  'not-streamable',
  'media-missing',
] as const;
export const unavailableReasonSchema = z.enum(UNAVAILABLE_REASONS);
export type UnavailableReason = z.infer<typeof unavailableReasonSchema>;

export const publicAvailabilitySchema = z.discriminatedUnion('status', [
  z.strictObject({
    status: z.literal('playable'),
    /** Caption locales permitted by the active grant and actually present. */
    captionLocales: z.array(localeSchema),
  }),
  z.strictObject({
    status: z.literal('unavailable'),
    reason: unavailableReasonSchema,
    /** For `rights-not-started`: when the window opens. */
    availableFrom: isoDateTimeSchema.nullable(),
  }),
]);
export type PublicAvailability = z.infer<typeof publicAvailabilitySchema>;

/** Playback sources, present only for items that are playable in the selected country (Phase 5). */
export const publicMediaSchema = z.strictObject({
  /** Same-origin /media/ path; only hosted assets play. */
  src: hostedPathSchema,
  mimeType: z.enum(MEDIA_MIME_TYPES),
  poster: hostedPathSchema.nullable(),
  /** Only tracks that exist AND that the active grant permits. */
  captions: z.array(
    z.strictObject({
      locale: localeSchema,
      kind: z.enum(['captions', 'subtitles']),
      src: hostedPathSchema,
    }),
  ),
});
export type PublicMedia = z.infer<typeof publicMediaSchema>;

export const publicCatalogueItemSchema = z.strictObject({
  id: entityIdSchema,
  title: z.string(),
  description: z.string(),
  /** Locale the text is actually in; differs from the requested locale when falling back. */
  textLocale: localeSchema,
  format: contentFormatSchema,
  series: seriesRefSchema.nullable(),
  ageMin: ageSchema,
  ageMax: ageSchema,
  durationSeconds: z.int().min(1),
  topics: z.array(interestTopicSchema),
  contentWarnings: z.array(contentWarningSchema),
  audioLocales: z.array(localeSchema),
  origin: z.strictObject({ broadcasterId: entityIdSchema, displayName: z.string() }),
  credits: z.array(creditSchema),
  /** Only set when the linked mission is published. */
  missionId: entityIdSchema.nullable(),
  /** The linked published game, with its title in one locale (fallback English). */
  /** Linked published mission, with its learning objectives (for "What you'll find out"). */
  mission: z
    .strictObject({
      id: entityIdSchema,
      title: z.string(),
      titleLocale: localeSchema,
      objectiveIds: z.array(objectiveIdSchema),
    })
    .nullable(),
  media: publicMediaSchema.nullable(),
  availability: publicAvailabilitySchema,
  isFictional: z.boolean(),
});
export type PublicCatalogueItem = z.infer<typeof publicCatalogueItemSchema>;

export const publicCatalogueSchema = z.strictObject({
  broadcaster: z.strictObject({
    id: entityIdSchema,
    displayName: z.string(),
    country: countryCodeSchema,
    supportedLocales: z.array(localeSchema),
    theme: broadcasterThemeSchema,
    enabledModules: z.array(broadcasterModuleSchema),
    isFictional: z.boolean(),
  }),
  /** Effective UI/content locale after applying broadcaster support. */
  locale: localeSchema,
  generatedAt: isoDateTimeSchema,
  items: z.array(publicCatalogueItemSchema),
});
export type PublicCatalogue = z.infer<typeof publicCatalogueSchema>;
